import { reactive } from "vue";
import router from "../router/index";
import { getAuth } from "firebase/auth";
import { getDatabase, ref, get } from "firebase/database";
import { applyNativeLanguageLocale, forgetCachedLocale } from "@/locales";
import { noteSignedInUid } from "@/composables/accountDeletionState.js";
import {
  beginPlaybackRateRead,
  forgetPlaybackRate,
  playbackRateReadIsCurrent,
} from "@/scripts/playbackRate.js";

let i18nInstance = null;

export function initAuthI18n(instance) {
  i18nInstance = instance;
}

// Note: this is stored in memory so refreshing the page will clear the state
// docs: https://vuejs.org/guide/scaling-up/state-management.html#simple-state-management-with-reactivity-api
export const authStore = reactive({
  userId: null,
  idToken: null,
  nativeLanguage: null, // User's native language name (e.g., "English (American)")

  getUserId() {
    return this.userId;
  },
  setUserId(userId) {
    this.userId = userId;
    // Every auth-state path in the app (setupAuth's onIdTokenChanged callback,
    // setupAuthPresence, the sign-in handlers, signOut) reports the account
    // through here, which makes this the one honest observer of "who is signed
    // in now". The account-deletion flag's reset rule rides on it rather than
    // on any view's lifecycle: DashboardView's users/{uid} listener binds once
    // per mount and never sees a uid change. Signing out passes null, which
    // deliberately does NOT reset the flag (see accountDeletionState.js).
    noteSignedInUid(userId);
  },
  async getIdToken() {
    const user = getAuth().currentUser;
    if (user) {
      return await user.getIdToken();
    }
    if (typeof window !== "undefined" && window.Capacitor?.isNativePlatform?.() === true) {
      try {
        const plugin = window.Capacitor.Plugins.FirebaseAuthentication;
        const { token } = await plugin.getIdToken();
        this.idToken = token;
        return token;
      } catch {
        // fall through to stored token
      }
    }
    return this.idToken;
  },
  setIdToken(idToken) {
    this.idToken = idToken;
  },
  checkAuthOrElseRedirect() {
    if (!this.userId) {
      router.push("/");
      return false;
    }
    return true;
  },

  /**
   * Set the user's native language and update the i18n locale.
   *
   * Async because locale bundles are lazily loaded: the switch has to wait for
   * the chunk, otherwise the UI stays English with nothing reported. Never
   * rejects, so the several callers that do not await it cannot produce an
   * unhandled rejection.
   *
   * `forUserId` is the account the value was read from or written for. Every
   * caller reaches this after an await on RTDB, and on a shared browser the
   * account can change during that await: a profile read fired for user A can
   * land after A signed out and B signed in. Without the check, A's language
   * would be applied to B's session and cached as B's. Callers that genuinely
   * have no account context (there are none today) may omit it.
   *
   * @param {string} languageName - Language name from Firebase (e.g., "English (American)")
   * @param {{forUserId?: string}} [options] - the uid this value belongs to
   * @returns {Promise<void>} resolves once the locale is live
   */
  async setNativeLanguage(languageName, { forUserId } = {}) {
    const belongsToCurrentUser = () =>
      forUserId === undefined || forUserId === this.userId;

    if (!belongsToCurrentUser()) {
      console.debug(
        "Ignoring a native-language update for a user who is no longer signed in"
      );
      return;
    }

    this.nativeLanguage = languageName;

    // nativeLanguage is the single source of truth for the UI locale, so this
    // is the one writer entitled to refresh the preferredLocale cache from an
    // account. applyNativeLanguageLocale owns both the mapping and the cache
    // rule; see the invariant in src/locales/index.js.
    //
    // The check is handed down rather than only made above, because the locale
    // chunk download sits between the two and can outlast the session: the
    // account has to still be the signed-in one at the moment the locale is
    // applied, not merely when the load started.
    if (i18nInstance) {
      await applyNativeLanguageLocale(languageName, {
        isStillValid: belongsToCurrentUser,
      });
    }
  },

  /**
   * Forget the signed-out account's language, in memory and in the cache.
   *
   * The in-memory value must go: leaving it would let the next person on this
   * browser see the previous account's language in AccountView and the conflict
   * modal before their own profile lands.
   *
   * The cache goes for the stronger reason that it no longer has any valid
   * contents. It holds either a signed-in user's nativeLanguage or a logged-out
   * visitor's own pick, and after a sign-out it is neither. Keeping it would
   * hand the previous user's language to the next one, and loadUserProfile
   * cannot correct that for an account that has no nativeLanguage yet.
   *
   * The live locale deliberately stays, so signing out does not flip the page
   * the user is looking at into another language. The cost is one English frame
   * on their next visit, which the lazy-loaded bundles already produce on a cold
   * load, and their own profile restores their language a moment later.
   */
  clearNativeLanguage() {
    this.nativeLanguage = null;
    forgetCachedLocale();
  },

  /**
   * Forget the signed-out account's playback speed, in memory and in the cache.
   *
   * Cleared for the same reason the locale cache is: on a shared device the
   * next person to sign in must start at 1x rather than at the previous
   * account's speed, and an account with no stored speed has nothing that would
   * correct it. It also cancels a mirror write still waiting for the chip to
   * rest, so the previous account's choice can never land in the next one's
   * profile. See src/scripts/playbackRate.js.
   */
  clearPlaybackRate() {
    forgetPlaybackRate();
  },

  /**
   * Load the user's profile preferences (native language, theme and playback
   * speed) from Firebase. Called after authentication; applies the UI locale
   * and returns the stored theme and playback speed so the caller can apply
   * them without re-writing RTDB.
   * @returns {Promise<{theme: string|null, audioPlaybackRate: *}>}
   */
  async loadUserProfile() {
    const forUserId = this.userId;
    if (!forUserId) {
      console.warn('Cannot load user profile: userId is null');
      return {};
    }

    const nativeLanguageAtReadStart = this.nativeLanguage;
    const playbackRateRead = beginPlaybackRateRead(forUserId);

    try {
      const db = getDatabase();
      // Three targeted reads fired together — they pipeline over the single
      // RTDB websocket (~one roundtrip) without pulling the whole users/{uid}
      // subtree.
      const [languageSnapshot, themeSnapshot, playbackRateSnapshot] = await Promise.all([
        get(ref(db, `users/${forUserId}/nativeLanguage`)),
        get(ref(db, `users/${forUserId}/theme`)),
        get(ref(db, `users/${forUserId}/audioPlaybackRate`)),
      ]);

      // A read is never newer than a write. If this same account wrote a native
      // language while this read was in flight (AccountView, the conflict modal,
      // onboarding), the snapshot in hand is already stale and applying it would
      // roll the live locale and the cache back. The uid check above catches a
      // different account; this catches the same one.
      if (languageSnapshot.exists() && this.nativeLanguage === nativeLanguageAtReadStart) {
        // Deliberately not awaited. The caller uses the returned theme to avoid
        // a light/dark flash, and setNativeLanguage now waits on a locale chunk
        // (webpack gives up only after chunkLoadTimeout, 120s by default).
        // Blocking the theme on that would trade a brief language flash for a
        // long theme flash. setNativeLanguage never rejects, so this cannot
        // become an unhandled rejection.
        this.setNativeLanguage(languageSnapshot.val(), { forUserId });
      }
      // The same rule for the speed chip: a choice made on this device that
      // was still waiting to be mirrored when the read began, a tap while it
      // was in flight, a write sent meanwhile, or a sign-out all make the
      // learner's own choice newer than this answer, and it is dropped.
      const audioPlaybackRate =
        playbackRateSnapshot.exists() &&
        playbackRateReadIsCurrent(playbackRateRead) &&
        this.userId === forUserId
          ? playbackRateSnapshot.val()
          : null;
      return {
        theme: themeSnapshot.exists() ? themeSnapshot.val() : null,
        audioPlaybackRate,
      };
    } catch (error) {
      console.error('Error loading user profile:', error);
      return {};
    }
  },
});
